import {jsx, ParamComponent} from 'panel';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import type {VNode} from 'snabbdom';

import {debounce} from 'common/util/lodash-wrapped';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {reporter} from 'common/report/error-reporting';
import {BrowseEntityTypeFilter, searchUtils} from 'common/report/util/search';
import {
  BrowseTrackedEvent,
  ContentDiscoveryType,
  getSearchCommonTrackingProperties,
  getTrackingPropertiesForResult,
  getTrackingPropertiesForResultsLoad,
  SearchSource,
  SearchTrackedProperty,
} from 'common/report/util/search/tracking';
import {getDashboardAllowedReports} from 'common/report/util/boards';
import {
  DEFAULT_SEARCH_QUERY_STRING,
  getOrderedSearchContextDisplayOptions,
  getReportNameFromSearchContext,
} from 'common/report/util/search';
import {
  RecentSearchItem,
  RecentSearchItemType,
  SearchQueryFilters,
  SearchResult,
  SearchSortKey,
  SearchSortOption,
  SearchSortOrder,
} from 'common/report/util/search/types';
import {EntityTableEl} from 'common/widgets/entity-collection/entity-table';
import {IronContextRegistry} from 'common/report/context-registry';
import {UserLocalStorageHelper} from 'common/report/user-local-storage';
import {BrowseMode} from 'common/widgets/browse/types';
import {SelectTheme} from 'common/components/select/constants';
import {SelectEl} from 'common/components/select';
import {ButtonEl} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {TextHighlightEl} from 'common/components/text-highlight';
import {ToggleEl} from 'common/components/toggle';
import {ToggleSize, ToggleTheme} from 'common/components/toggle/types';
import {pluralize} from 'common/util/string';
import {LocalStorageKey} from 'common/report/constants';
import {panel2react} from 'common/util/react';

import {EntityGalleryEl} from '../entity-collection/entity-gallery';
import {BrowseQueryParams, FilterType, ShareStatusFilter} from './types';
import {CAPTURED_TRACKING} from './tracking';
import css from './index.inline.styl';
import {BrowseFilterEl} from './browse-filters';
import {BrowseSuggestedCreatorsEl} from './browse-suggested-creators';
import browseFilterCss from './browse-filters/index.module.styl?inline';
import {ErrorEmptyState, NoResultsEmptyState} from './empty-states';

const SEARCH_LIMIT = 100;

const DEFAULT_QUERY_PARAMS = {
  searchedText: DEFAULT_SEARCH_QUERY_STRING,
  filter: {
    [FilterType.CreatorEmail]: [],
    [FilterType.ShareStatus]: [],
    [FilterType.Report]: [],
  },
  sort: SearchSortOption.Popularity,
};

export interface BrowseParams {
  creatorFilter?: string;
  entityTypeFilter: BrowseEntityTypeFilter;
  mode: BrowseMode;
  projectId: number;
  onCreateDashboardClicked?: () => void;
  onOpenTemplateModalClicked?: (options: {entryPoint: string}) => void;
  onOpenFoundationalTemplateModalClicked?: (options: {entryPoint: string}) => void;
  shouldUseFoundationalTemplates?: boolean;
  onRedirectToEntity?: () => void;
  onSearchUpdated?: ({trackingProps}: {trackingProps: any}) => void;
  onSelect?: (result: SearchResult) => void;
  searchText?: string;
  userId: number;
  headerContent?: VNode;
}

type JsxProps = {
  params: BrowseParams;
};

enum BrowseQueryState {
  Done = `done`,
  Failed = `failed`,
  Loading = `loading`,
}

enum BrowseResultsMode {
  Table = `table`,
  Gallery = `gallery`,
}

type BrowseState = {
  lastSearchedText: string;
  name: string;
  entities: SearchResult[];
  queryParams: BrowseQueryParams;
  queryRequestIndex: number;
  queryState: BrowseQueryState;
  resultsMode: BrowseResultsMode;
};

export type BrowsePersistedState = {
  queryParams: Partial<BrowseQueryParams>;
  resultsMode: BrowseResultsMode;
};

const FilterLabel = ({cmp}: {cmp: Browse}) => {
  const numActiveFilters = cmp.getNumActiveFilters();

  return (
    <div sel='.filter-label'>
      Filter
      {numActiveFilters > 0 && (
        <TextHighlightEl
          sel='.filter-label-num'
          attrs={{
            length: `wide`,
            text: numActiveFilters.toString(),
          }}
        />
      )}
    </div>
  );
};

const SortControl = ({cmp}: {cmp: Browse}) => {
  let sortOptions: SearchSortOption[];
  if (cmp.state.lastSearchedText !== DEFAULT_SEARCH_QUERY_STRING) {
    sortOptions = [
      SearchSortOption.Relevance,
      SearchSortOption.Popularity,
      SearchSortOption.LastModified,
      SearchSortOption.TitleAscending,
      SearchSortOption.TitleDescending,
    ];
  } else {
    sortOptions = [
      SearchSortOption.Popularity,
      ...(cmp.getContext(`projectTopCreators`).hasViewedContent() ? [SearchSortOption.RecentlyViewed] : []),
      SearchSortOption.LastModified,
      SearchSortOption.TitleAscending,
      SearchSortOption.TitleDescending,
    ];
  }

  return (
    <SelectEl
      sel='.sort-menu'
      attrs={{
        elref: `sortMenu`,
        icon: {leftIcon: `sort`},
        label: cmp.state.queryParams.sort,
        'menu-width': 150,
        theme: SelectTheme.Tertiary,
      }}
      props={{
        sections: [
          {
            items: sortOptions.map((sortOption) => ({
              label: sortOption,
              value: sortOption,
              isSelected: cmp.state.queryParams.sort === sortOption,
            })),
          },
        ],
      }}
      on={{
        change: (ev) => {
          cmp.track(BrowseTrackedEvent.Sort, {'New Sort Option': ev.detail.item.value});
          cmp.updateQueryParams({sort: ev.detail.item.value});
        },
      }}
    />
  );
};

const BrowseResultsHeader = ({cmp}: {cmp: Browse}) => {
  const isLoading = cmp.state.queryState === BrowseQueryState.Loading;
  const entitiesCount = cmp.state.entities?.length || 0;
  const resultsLabel = entitiesCount > 0 ? `${entitiesCount} ${pluralize(`result`, entitiesCount)}` : ``;

  return (
    <div sel='.browse-results-header'>
      <div sel='.browse-results-count'>{!isLoading && resultsLabel}</div>
      <div sel='.browse-results-controls'>
        <div sel='.browse-results-sort'>
          <SortControl cmp={cmp} />
        </div>
        {cmp.isFullPageMode() && (
          <div sel='.browse-results-filter'>
            <ToggleEl
              attrs={{
                size: ToggleSize.Medium,
                theme: ToggleTheme.SegmentedController,
                selected: cmp.state.resultsMode,
                options: [
                  {label: ``, icon: `icon-grid`, value: BrowseResultsMode.Gallery},
                  {label: ``, icon: `type-list`, value: BrowseResultsMode.Table},
                ],
                elref: `resultsModeToggle`,
              }}
              on={{
                select: (ev: CustomEvent) => {
                  cmp.track(BrowseTrackedEvent.SwitchedResultMode, {
                    [SearchTrackedProperty.ResultMode]: ev.detail.selected,
                  });
                  cmp.update({resultsMode: ev.detail.selected as BrowseResultsMode});
                  cmp.updatePersistedState();
                },
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const BrowseResults = ({cmp}: {cmp: Browse}) => {
  const {queryParams, queryState} = cmp.state;
  const isLoading = queryState === BrowseQueryState.Loading;

  if (cmp.state.queryState === BrowseQueryState.Failed) {
    return <ErrorEmptyState />;
  }

  if (!isLoading && !cmp.state.entities?.length) {
    return <NoResultsEmptyState cmp={cmp} />;
  }

  return (
    <div sel='.browse-results-table'>
      {cmp.state.resultsMode === BrowseResultsMode.Table ? (
        <EntityTableEl
          params={{
            mode: cmp.params.mode,
            isLoading: queryState === BrowseQueryState.Loading,
            nameLabel: queryParams.sort,
            userId: cmp.params.userId,
            entities: cmp.state.entities,
            onEntityClick: (entity) => cmp.handleEntityClick(entity),
            onEntitySelect: (entity) => cmp.params.onSelect?.(entity),
            onEntityRedirect: cmp.params.onRedirectToEntity,
          }}
        />
      ) : (
        <EntityGalleryEl
          params={{
            mode: cmp.params.mode,
            isLoading: queryState === BrowseQueryState.Loading,
            nameLabel: queryParams.sort,
            userId: cmp.params.userId,
            entities: cmp.state.entities,
            onEntityClick: (entity) => cmp.handleEntityClick(entity),
          }}
          attrs={{
            elref: `entityGallery`,
          }}
        />
      )}
    </div>
  );
};

export class Browse extends ParamComponent<BrowseParams, BrowseState, IronContextRegistry> {
  private persistedStateStorage: UserLocalStorageHelper<BrowsePersistedState>;

  get config() {
    return {
      contexts: [`currentProject` as const, `sessionStorageManager` as const, `projectTopCreators` as const],
      params: {
        creatorFilter: String,
        emptyMessage: String,
        entityTypeFilter: String,
        headerContent: Object,
        mode: String,
        onCreateDashboardClicked: Function,
        onOpenFoundationalTemplateModalClicked: Function,
        onOpenTemplateModalClicked: Function,
        onRedirectToEntity: Function,
        onSearchUpdated: Function,
        onSelect: Function,
        projectId: Number,
        searchText: String,
        shouldUseFoundationalTemplates: Boolean,
        userId: Number,
      },
      useShadowDom: true,
      css: css + browseFilterCss,
      defaultState: {
        entities: [],
        lastSearchedText: DEFAULT_SEARCH_QUERY_STRING,
        name: ``,
        open: false,
        queryParams: {
          filter: {
            [FilterType.CreatorEmail]: [],
            [FilterType.Report]: [],
            [FilterType.ShareStatus]: [],
          },
          sort: SearchSortOption.Popularity,
          searchedText: DEFAULT_SEARCH_QUERY_STRING,
        },
        queryRequestIndex: 0,
        queryState: BrowseQueryState.Loading,
        resultsMode: BrowseResultsMode.Table,
      },
      template: () => {
        const title = this.getTitle();

        return (
          <div sel='.browse-container' class={{'is-full-page': this.isFullPageMode(), 'is-add-mode': this.isAddMode()}}>
            {title && <h2 sel='.browse-title'>{title}</h2>}
            <div sel='.browse-sections-container'>
              <div sel='.browse-main-section'>
                <section sel='.browse-header'>{this.params.headerContent}</section>
                {/* We show the suggested creators in full page mode, only before the user makes a query */}
                {this.isFullPageMode() && this.getNumActiveFilters() === 0 && (
                  <div sel='.browse-suggested-creators'>
                    <BrowseSuggestedCreatorsEl
                      params={{
                        onCreatorClick: (creator, idx) => {
                          this.updateQueryParams({
                            filter: {...this.state.queryParams.filter, [FilterType.CreatorEmail]: [creator.email]},
                          });
                          this.track(BrowseTrackedEvent.SuggestedCreatorSelected, {position: idx});
                        },
                        userId: this.params.userId,
                      }}
                    />
                  </div>
                )}
                <div sel='.browse-results'>
                  <BrowseResultsHeader cmp={this} />
                  <BrowseResults cmp={this} />
                </div>
              </div>
              <div sel='.browse-filter-section'>
                <div sel='.browse-filter-section-header'>
                  <FilterLabel cmp={this} />
                  {this.getNumActiveFilters() > 0 && (
                    <ButtonEl
                      sel='.clear-button'
                      attrs={{theme: ButtonTheme.Tertiary}}
                      on={{click: () => this.resetFilters()}}
                    >
                      Clear all
                    </ButtonEl>
                  )}
                </div>
                <BrowseFilterEl
                  params={{
                    isAddMode: this.isAddMode(),
                    filter: this.state.queryParams.filter,
                    onFilterChange: (filter) => this.updateQueryParams({filter}),
                    userId: this.params.userId,
                  }}
                />
              </div>
            </div>
          </div>
        );
      },
      hooks: {
        preUpdate: (_stateUpdate, paramsUpdate?: BrowseParams) => {
          if (
            (!isNil(paramsUpdate?.searchText) && paramsUpdate.searchText !== this.params.searchText) ||
            !isNil(paramsUpdate?.creatorFilter) ||
            !isNil(paramsUpdate?.entityTypeFilter)
          ) {
            this.setDefaultState(paramsUpdate);
          }
        },
      },
    };
  }

  getCurrentProject() {
    return this.getContext(`currentProject`).get().value;
  }

  getNumActiveFilters() {
    return Object.values(this.state.queryParams.filter).reduce((acc, curr) => {
      return acc + curr.length;
    }, 0);
  }

  connectedCallback() {
    super.connectedCallback();

    this.persistedStateStorage = new UserLocalStorageHelper({
      namespace: LocalStorageKey.BrowsePersistedState,
      context: this.getContext(`sessionStorageManager`),
    });

    this.setDefaultState(this.params);
    this.track(BrowseTrackedEvent.OpenDiscover);
  }

  isAddMode() {
    return this.params.mode === BrowseMode.Add;
  }

  isFullPageMode() {
    return this.params.mode === BrowseMode.Discover;
  }

  getTitle(): string {
    if (this.params.mode === BrowseMode.Discover) {
      return this.params.searchText ? `Search results for “${this.params.searchText}”` : null;
    }

    return {
      [BrowseMode.Add]: `Add Existing Report`,
      [BrowseMode.Browse]: `Browse`,
    }[this.params.mode];
  }

  getAvailableReports() {
    let reports = getOrderedSearchContextDisplayOptions();
    if (this.isAddMode()) {
      reports = reports.filter((report) => {
        const reportFromContext = getReportNameFromSearchContext(report);
        return getDashboardAllowedReports().includes(reportFromContext);
      });
    }
    return reports;
  }

  track(event: string, props: Obj = {}) {
    if (CAPTURED_TRACKING.has(event)) {
      mixpanel.track(event, {
        ...this.getCommonTrackingProperties(),
        ...props,
        project_id: this.params.projectId,
      });
    }
  }

  resetFilters() {
    this.updateQueryParams({
      filter: {[FilterType.ShareStatus]: [], [FilterType.Report]: [], [FilterType.CreatorEmail]: []},
    });
  }

  getCommonTrackingProperties() {
    const {entities} = this.state;
    const searchedText = this.params.searchText || ``;

    const searchSource = this.isFullPageMode() ? SearchSource.Discover : SearchSource.Modal;

    return {
      ...getSearchCommonTrackingProperties(),
      [SearchTrackedProperty.ContentDiscoveryType]: searchedText
        ? ContentDiscoveryType.SearchQuery
        : ContentDiscoveryType.Browse,
      [SearchTrackedProperty.SortValue]: this.state.queryParams.sort,
      [SearchTrackedProperty.Source]: searchSource,
      [SearchTrackedProperty.ResultAmount]: entities?.length || 0,
      [SearchTrackedProperty.SearchQuery]: searchedText,
      [SearchTrackedProperty.BrowseMode]: this.params.mode,
      [SearchTrackedProperty.NumCreatorFilters]: this.state.queryParams.filter[FilterType.CreatorEmail].length,
      [SearchTrackedProperty.ResultMode]: this.state.resultsMode,
    };
  }

  getQueryFilters(): SearchQueryFilters {
    const {queryParams} = this.state;
    let queryTypes = queryParams.filter.report;
    if (!queryTypes.length) {
      queryTypes = this.getAvailableReports();
    }
    const queryTypeFilters = queryTypes.map((value) => ({value, negated: false}));
    const shareStatusSet = new Set(queryParams.filter[FilterType.ShareStatus]);
    const queryUserFilters = queryParams.filter[FilterType.CreatorEmail].map((value: string) => ({
      value,
      negated: false,
    }));

    if (shareStatusSet.has(ShareStatusFilter.Shared) && !shareStatusSet.has(ShareStatusFilter.Owner)) {
      queryUserFilters.push({
        value: `me`,
        negated: true,
      });
    }

    if (shareStatusSet.has(ShareStatusFilter.Owner) && !shareStatusSet.has(ShareStatusFilter.Shared)) {
      queryUserFilters.push({
        value: `me`,
        negated: false,
      });
    }

    return {
      email: queryUserFilters,
      type: queryTypeFilters,
    };
  }

  getQuerySortParams(sort: SearchSortOption) {
    switch (sort) {
      case SearchSortOption.Popularity:
        return {
          key: SearchSortKey.Popularity,
          order: SearchSortOrder.Descending,
        };
      case SearchSortOption.LastModified:
        return {
          key: SearchSortKey.LastModified,
          order: SearchSortOrder.Descending,
        };
      case SearchSortOption.TitleAscending:
        return {
          key: SearchSortKey.Name,
          order: SearchSortOrder.Ascending,
        };
      case SearchSortOption.TitleDescending:
        return {
          key: SearchSortKey.Name,
          order: SearchSortOrder.Descending,
        };
      case SearchSortOption.RecentlyViewed:
        return {
          key: SearchSortKey.RecentlyViewed,
          order: SearchSortOrder.Descending,
        };
      default:
        return null;
    }
  }

  updateQueryParams(newQueryParams: Partial<BrowseQueryParams>, options: {customSearchText?: string} = {}) {
    this.update({
      queryParams: {
        ...this.state.queryParams,
        ...newQueryParams,
      },
    });
    this.fetchEntities(options.customSearchText);

    this.updatePersistedState();
  }

  setDefaultState(paramUpdate: BrowseParams) {
    // searchText=`` means "I don't want to set a search text".
    // searchText=null means "I want to set the search text to the default (persisted) value".
    // same for other filters

    const persistedState = this.getPersistedState();

    const persistedQueryParams: Partial<BrowseQueryParams> = persistedState?.queryParams || {filter: {}};

    // only set the params if they are not null or undefined
    const paramsQueryParams = {
      filter: {
        ...(!isNil(paramUpdate.creatorFilter)
          ? {[FilterType.CreatorEmail]: paramUpdate.creatorFilter === `` ? [] : [paramUpdate.creatorFilter]}
          : {}),
        ...(!isNil(paramUpdate.entityTypeFilter)
          ? {[FilterType.Report]: paramUpdate.entityTypeFilter === `` ? [] : [paramUpdate.entityTypeFilter]}
          : {}),
      },
      // when the search text is updated, we need to set the sort to relevance
      ...(paramUpdate.searchText ? {sort: SearchSortOption.Relevance} : {}),
      // is searchText was set, but it was cleared, we need to set the sort to popularity
      ...(this.params.searchText && !paramUpdate.searchText ? {sort: SearchSortOption.Popularity} : {}),
    };

    // if paramsQueryParams values are not empty, it means that we received custom params
    const hasParamDefaults = !isEmpty(paramsQueryParams.filter);

    const resultsMode = persistedState && !hasParamDefaults ? persistedState.resultsMode : BrowseResultsMode.Table;

    const queryParams = {
      ...DEFAULT_QUERY_PARAMS,
      ...persistedQueryParams,
      ...paramsQueryParams,
      filter: {
        ...DEFAULT_QUERY_PARAMS.filter,
        ...persistedQueryParams.filter,
        ...paramsQueryParams.filter,
      },
    };

    this.update({
      resultsMode,
    });

    // searchText is still not set as the new params.searchText, so we need to set it manually
    this.updateQueryParams(queryParams, {customSearchText: paramUpdate.searchText});
  }

  async updatePersistedState() {
    if (this.isFullPageMode()) {
      const value: BrowsePersistedState = {
        queryParams: {
          ...this.state.queryParams,
          ...(this.state.queryParams.sort !== SearchSortOption.Relevance ? {sort: this.state.queryParams.sort} : {}),
        },
        resultsMode: this.state.resultsMode,
      };
      await this.persistedStateStorage.setItem({userId: this.params.userId, key: this.params.projectId, value});
    }
  }

  getPersistedState(): BrowsePersistedState | null {
    if (this.isFullPageMode()) {
      return this.persistedStateStorage.getItem({userId: this.params.userId, key: this.params.projectId});
    }
    return null;
  }

  private fetchEntities = debounce(
    async (customSearchText?: string) => {
      const currentRequestIndex = this.state.queryRequestIndex + 1;
      this.update({
        queryState: BrowseQueryState.Loading,
        queryRequestIndex: currentRequestIndex,
      });

      // clone query params because the reference could be updated while a search request is in flight
      const queryParams = cloneDeep(this.state.queryParams);
      const searchedText = isNil(customSearchText) ? this.params.searchText || `` : customSearchText;

      const trackingProps = {
        [SearchTrackedProperty.OwnerFilters]: queryParams.filter[FilterType.ShareStatus],
        [SearchTrackedProperty.ReportFilters]: queryParams.filter[FilterType.Report],
        [SearchTrackedProperty.SearchQuery]: searchedText,
        [SearchTrackedProperty.SortValue]: queryParams.sort,
      };

      const queryFilters = this.getQueryFilters();

      try {
        // always force empty queries to hit the search endpoint for latest results
        const results = await searchUtils.executeSearch({
          queryFilters,
          searchedText,
          querySort: this.getQuerySortParams(queryParams.sort),
          fromFetchCache: !!searchedText,
          limit: SEARCH_LIMIT,
        });

        if (currentRequestIndex === this.state.queryRequestIndex && results) {
          this.update({
            queryState: BrowseQueryState.Done,
            lastSearchedText: searchedText,
            entities: results,
          });

          // add searchedText to recent only after the query returned results succesfully
          if (searchedText !== DEFAULT_SEARCH_QUERY_STRING) {
            searchUtils.addRecentItem({
              icon: `search`,
              key: `${RecentSearchItemType.SearchTerm}-${searchedText}`,
              label: searchedText,
              type: RecentSearchItemType.SearchTerm,
            } as RecentSearchItem);
          }
        }

        this.track(BrowseTrackedEvent.SearchResultLoaded, {
          ...trackingProps,
          ...getTrackingPropertiesForResultsLoad(results),
        });
      } catch (err) {
        reporter.error(err);
        this.update({
          queryState: BrowseQueryState.Failed,
          entities: null,
        });
      }
    },
    200,
    {leading: true},
  );

  handleEntityClick(entity: SearchResult) {
    this.track(BrowseTrackedEvent.ResultSelected, {
      ...getTrackingPropertiesForResult(entity),
      [SearchTrackedProperty.ResultPosition]: this.state.entities.findIndex((result) => entity.id === result.id),
      [SearchTrackedProperty.ResultAmount]: this.state.entities.length,
    });
  }
}

export const BrowseEl = defineAndMakeJsxFactory<JsxProps>(`mp-browse`, Browse);
export const BrowseReact = panel2react<JsxProps>(`mp-browse`, Browse);
